import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { DocumentNode } from 'graphql';
import { SingleValue, StylesConfig } from 'react-select';
import { GET_ALL_MC1S, CREATE_MC1 } from '../../../graph-queries/maintenance/mc1';
import { GET_ALL_SH1S, CREATE_SH1 } from '../../../graph-queries/maintenance/sh1';
import { Mc1, Sh1 } from '../../../graph-queries/__generated__/graphql';

/**
 * Option interface used for both MC1 and SH1.
 */
export interface CertificationOption {
  label: string;
  value: string;
  id?: number;
}

type CertificationType = 'mc1' | 'sh1';

const queryMap: Record<CertificationType, DocumentNode> = {
  mc1: GET_ALL_MC1S,
  sh1: GET_ALL_SH1S,
};

const mutationMap: Record<CertificationType, DocumentNode> = {
  mc1: CREATE_MC1,
  sh1: CREATE_SH1,
};

/**
 * Maps a returned item (either Mc1 or Sh1) into the unified CertificationOption shape.
 */
const mapItemToOption = (
  type: CertificationType,
  item: Mc1 | Sh1
): CertificationOption => {
  if (type === 'mc1') {
    const { mc1Number, mc1Id } = item as Mc1;
    return { label: mc1Number, value: mc1Number, id: mc1Id };
  } else {
    const { sh1Number, sh1Id } = item as Sh1;
    return { label: sh1Number, value: sh1Number, id: sh1Id };
  }
};

/**
 * Custom hook to manage maintenance (MC1 or SH1) select options.
 * @param type - Either 'mc1' or 'sh1'
 * @param assetId - The assetId to filter records.
 * @param defaultOption - The default option (if any).
 * @returns An object containing the options array, the selected option, 
 *          and handlers for change and creating new entries.
 */
export const useCertificationSelect = (
  type: CertificationType,
  assetId: number,
  defaultOption?: CertificationOption
) => {
  const query: DocumentNode = queryMap[type];
  const mutation: DocumentNode = mutationMap[type];

  const { loading, data } = useQuery(query, {
    variables: { assetId },
    skip: !assetId || assetId === -1,
  });
  const [createMutation, { loading: creatingOption } ] = useMutation(mutation, {
    refetchQueries: [{ query, variables: { assetId } }],
    awaitRefetchQueries: true,
  });

  const [options, setOptions] = useState<CertificationOption[]>([]);
  const [value, setValue] = useState<CertificationOption | null>(defaultOption || null);

  const memoizedDefaultOption = useMemo(
    () => defaultOption,
    [defaultOption?.label, defaultOption?.value, defaultOption?.id]
  );

  useEffect(() => {
    if (!loading && data && assetId) {
      const items = type === 'mc1' ? data.allMC1s : data.allSH1s;
      const mappedOptions: CertificationOption[] = items.map((item: Mc1 | Sh1) =>
        mapItemToOption(type, item)
      );
      setOptions(mappedOptions);
      if (memoizedDefaultOption) {
        const found = mappedOptions.find(o => o.value === memoizedDefaultOption.value);
        setValue(found || memoizedDefaultOption);
      }
    }
  }, [loading, data, assetId, type, memoizedDefaultOption]);

  const handleCreate = async (inputValue: string) => {
    try {
      const response = await createMutation({
        variables: {
          input: {
            [`${type}Number`]: inputValue, // mc1Number or sh1Number
            assetId,
          },
        },
      });
      const newItem = type === 'mc1'
        ? response.data?.createMC1
        : response.data?.createSH1;
      if (!newItem) {
        throw new Error(`create${type.toUpperCase()} did not return a valid item`);
      }
      const newOption = mapItemToOption(type, newItem);
      setValue(newOption);
    } catch (error) {
      console.error(`Error creating ${type.toUpperCase()}:`, error);
    }
  };

  const handleChange = (
    newValue: SingleValue<CertificationOption>
  ) => {
    setValue(newValue);
  };

  return { options, value, handleChange, handleCreate, creatingOption };
};

interface SystemOption {
  value: string;
  label: string;
  systemId: number;
}

export const customSelectStyles: StylesConfig<SystemOption, false> = {
  control: (base) => ({
    ...base,
    minHeight: '38px',
    borderColor: '#ced4da',
  }),
  menu: (base) => ({
    ...base,
    maxHeight: '300px',
    zIndex: 9999,
    position: 'absolute',
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: '250px',
    overflowY: 'auto',
  }),
  option: (base, state) => ({
    ...base,
    padding: '8px 12px',
    fontSize: '14px',
    lineHeight: '1.5',
    backgroundColor: state.isSelected ? '#727476ff' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#212529',
    cursor: 'pointer',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: '14px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#6c757d',
  }),
};
